import { io } from "socket.io-client";
import { backendUrl } from "../Router/constant";
import { AUTH_TOKEN_KEY } from "../actions/auth";

// Training chat runs over Socket.IO on the `/chat` namespace, the same channel
// the widget uses. What separates the two is the handshake: training sends the
// owner's auth token plus `mode: "training"`, where the widget sends a
// visitorId. Ported from test-training-chat.html.

// backendUrl is an http(s) origin with a trailing slash; socket.io wants the
// origin and the namespace as separate arguments.
const socketOrigin = () => String(backendUrl || "").replace(/\/+$/, "");

/**
 * Opens a training connection.
 *
 * @param {string} assistantId
 * @param {object} handlers callbacks for the events the server emits
 * @param {string} conversationId an existing training conversation to resume
 * @returns {import("socket.io-client").Socket}
 */
export const connectTraining = (assistantId, handlers = {}, conversationId = "") => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);

  const socket = io(`${socketOrigin()}/chat`, {
    query: { assistantId },
    auth: { token, mode: "training" },
    transports: ["websocket", "polling"],
  });

  // The server won't create a conversation until asked. Naming one continues
  // it instead of opening a second training thread alongside it.
  socket.on("connect", () => {
    socket.emit("start_conversation", conversationId ? { conversationId } : {});
    handlers.onConnect?.();
  });

  // Sent when an existing training conversation is resumed — `messages`
  // carries whatever was said before.
  socket.on("conversation_started", (data) => {
    handlers.onStarted?.(data || {});
  });

  socket.on("conversation_created", (data) => {
    handlers.onCreated?.(data || {});
  });

  // Progress updates. `fact_learned` is the one that matters to a person:
  // it means the assistant committed something to memory.
  socket.on("stage", (data) => {
    handlers.onStage?.(data || {});
  });

  socket.on("final", (data) => {
    handlers.onFinal?.(data || {});
  });

  socket.on("error", (data) => {
    handlers.onError?.(data?.message || "Something went wrong.");
  });

  socket.on("connect_error", (err) => {
    handlers.onError?.(err?.message || "Connection rejected.", true);
  });

  socket.on("disconnect", () => {
    handlers.onDisconnect?.();
  });

  return socket;
};

/**
 * Sends a training message. The conversation id is included once the server
 * has told us one, so replies thread onto the same conversation.
 */
export const sendTrainingMessage = (socket, conversationId, message) => {
  if (!socket) return;

  socket.emit(
    "message",
    conversationId ? { conversationId, message } : { message },
  );
};
